import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Player } from '@lottiefiles/react-lottie-player';
import { isMobile, isIOS, isAndroid } from '@utils/helpers';
import { getConfig } from '@utils/config';
import { useAnalytics } from '@/providers/AnalyticsProvider';
import type { ServiceType } from '@/types';

export function RedirectPage() {
  const { service } = useParams<{ service: ServiceType }>();
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const [isRedirecting, setIsRedirecting] = React.useState(true);
  if (!service || (service !== 'vis' && service !== 'sls')) {
    navigate('/');
    return;
  }

  const config = getConfig();
  const info = config.SERVICES[service];

  if (!info) {
    navigate(`/`);
    return;
  }

  React.useEffect(() => {
    // Track redirect attempt
    trackEvent('app_redirect_attempt', {
      service_type: service,
      is_mobile: isMobile(),
      platform: isIOS() ? 'ios' : isAndroid() ? 'android' : 'web',
      environment: config?.ENVIRONMENT || 'unknown',
    });

    const attemptRedirect = () => {
      if (isMobile()) {
        // Try app redirect first
        window.location.href = `${info.appScheme}/${info.callPagePath}${window.location.search}`;

        // Set a timeout to check if the redirect worked
        setTimeout(() => {
          setIsRedirecting(false);
          trackEvent('app_redirect_fallback', {
            service_type: service,
            platform: isIOS() ? 'ios' : isAndroid() ? 'android' : 'web',
          });
        }, 2500);
      } else {
        window.location.href = `${info.web}/${info.callPagePath}${window.location.search}`;
      }
    };

    // Small delay to show loading animation
    const timer = setTimeout(attemptRedirect, 1000);

    return () => clearTimeout(timer);
  }, [service, navigate, trackEvent]);

  return (
    <section
      className="w-full min-h-dvh flex items-center justify-center px-4"
      aria-label="Redirecting to app"
    >
      {isRedirecting ? (
        <div className="text-center py-8">
          <Player
            autoplay
            loop
            src="/animations/loading.json"
            className="h-52 w-52 mx-auto mb-8 invert"
          />
          <h1 className="text-2xl font-bold mb-4">Opening {info.name}...</h1>
          <p className="text-base text-gray-600 mb-8">Redirecting you to {info.name}</p>
        </div>
      ) : (
        <DownloadButtons service={service} />
      )}
    </section>
  );
}

function DownloadButtons({ service }: { service: ServiceType }) {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();

  const config = getConfig();
  const serviceConfig = config!.SERVICES[service!];

  const handleDownloadClick = (store: 'app' | 'play') => {
    let url = '';
    if (store === 'app') {
      url = serviceConfig?.appStore;
    } else {
      url = serviceConfig?.playStore;
    }

    // Track download button click
    trackEvent('download_button_click', {
      store_type: store,
      service_type: service,
      is_mobile: isMobile(),
      platform: isIOS() ? 'ios' : isAndroid() ? 'android' : 'web',
      environment: config?.ENVIRONMENT || 'unknown',
    });

    window.open(url, '_blank');
  };

  return (
    <section className="w-full">
      <div className="px-3 text-center">
        <img
          src={serviceConfig.logo}
          alt={`${serviceConfig.name} logo`}
          className="h-10 w-auto mx-auto mb-6"
        />

        <p className="text-gray-600 text-lg mb-8">{serviceConfig.description}</p>

        <p className="text-xl font-semibold text-center mb-6">Get {serviceConfig.name}</p>

        <div className="flex flex-col items-center gap-4 mb-8">
          {(isIOS() || !isMobile()) && (
            <button
              onClick={() => handleDownloadClick('app')}
              className="p-0 bg-transparent hover:opacity-90 transition"
            >
              <img
                src="/images/app-store.svg"
                alt="Download on App Store"
                className="h-10 w-auto"
              />
            </button>
          )}

          {(isAndroid() || !isMobile()) && (
            <button
              onClick={() => handleDownloadClick('play')}
              className="p-0 bg-transparent hover:opacity-90 transition"
            >
              <img
                src="/images/play-store.svg"
                alt="Get it on Google Play"
                className="h-10 w-auto"
              />
            </button>
          )}
        </div>

        <button
          onClick={() => navigate('/')}
          className="text-blue-600 underline hover:no-underline"
        >
          ← Back to service selection
        </button>
      </div>
    </section>
  );
}
